import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Trash2, Plus, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import SocialMediaSync from "./SocialMediaSync";
import { ImageUpload } from "./ImageUpload";

interface SocialMediaPost {
  id: number;
  platform: string;
  post_id: string;
  caption: string;
  media_url: string;
  media_type: string;
  post_url: string;
  likes_count: number;
  comments_count: number;
  posted_at: string;
  is_featured: boolean;
  is_active: boolean;
}

const SocialMediaPostsManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingPost, setEditingPost] = useState<SocialMediaPost | null>(null);
  const [newPost, setNewPost] = useState({
    platform: "",
    post_id: "",
    caption: "",
    media_url: "",
    media_type: "image",
    post_url: "",
    likes_count: 0,
    comments_count: 0,
    posted_at: new Date().toISOString().split('T')[0],
    is_featured: false,
  });

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["social-media-posts-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_social_media_posts")
        .select("*")
        .order("posted_at", { ascending: false });
      
      if (error) throw error;
      return data as SocialMediaPost[];
    }
  });

  const savePost = useMutation({
    mutationFn: async () => {
      if (editingPost) {
        const { error } = await supabase
          .from("sns_social_media_posts")
          .update({
            ...newPost,
            posted_at: new Date(newPost.posted_at).toISOString(),
          })
          .eq("id", editingPost.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("sns_social_media_posts")
          .insert([{
            ...newPost,
            posted_at: new Date(newPost.posted_at).toISOString(),
          }]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["social-media-posts-admin"] });
      queryClient.invalidateQueries({ queryKey: ["social-media-posts"] });
      resetForm();
      toast({
        title: "Success",
        description: editingPost ? "Post updated successfully" : "Post added successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to ${editingPost ? "update" : "add"} post: ` + error.message,
        variant: "destructive",
      });
    },
  });

  const toggleStatus = useMutation({
    mutationFn: async ({ id, field, value }: { id: number; field: string; value: boolean }) => {
      const { error } = await supabase
        .from("sns_social_media_posts")
        .update({ [field]: value })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["social-media-posts-admin"] });
      queryClient.invalidateQueries({ queryKey: ["social-media-posts"] });
    },
  });

  const deletePost = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from("sns_social_media_posts")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["social-media-posts-admin"] });
      queryClient.invalidateQueries({ queryKey: ["social-media-posts"] });
      toast({
        title: "Success",
        description: "Post deleted successfully",
      });
    },
  });

  const resetForm = () => {
    setNewPost({
      platform: "",
      post_id: "",
      caption: "",
      media_url: "",
      media_type: "image",
      post_url: "",
      likes_count: 0,
      comments_count: 0,
      posted_at: new Date().toISOString().split('T')[0],
      is_featured: false,
    });
    setEditingPost(null);
  };

  const handleEdit = (post: SocialMediaPost) => {
    setEditingPost(post);
    setNewPost({
      platform: post.platform,
      post_id: post.post_id,
      caption: post.caption,
      media_url: post.media_url,
      media_type: post.media_type,
      post_url: post.post_url,
      likes_count: post.likes_count,
      comments_count: post.comments_count,
      posted_at: new Date(post.posted_at).toISOString().split('T')[0],
      is_featured: post.is_featured,
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-8">
      <Tabs defaultValue="sync" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="sync">Auto Sync</TabsTrigger>
          <TabsTrigger value="manual">Manual Management</TabsTrigger>
        </TabsList>
        
        <TabsContent value="sync" className="space-y-6">
          <SocialMediaSync />
          
          <Card>
            <CardHeader>
              <CardTitle>API Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">Instagram Setup:</h4>
                <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
                  <li>Create a Facebook Developer App</li>
                  <li>Add Instagram Basic Display product</li>
                  <li>Generate a long-lived access token</li>
                  <li>Add INSTAGRAM_ACCESS_TOKEN to your edge function secrets</li>
                </ol>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Facebook Setup:</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Use the same Facebook Developer App</li>
                  <li>Generate a Page Access Token</li>
                  <li>Add FACEBOOK_ACCESS_TOKEN and FACEBOOK_PAGE_ID to your edge function secrets</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {editingPost ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  {editingPost ? "Edit Social Media Post" : "Add New Social Media Post"}
                </div>
                {editingPost && (
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Platform</label>
                  <Select
                    value={newPost.platform}
                    onValueChange={(value) => setNewPost(prev => ({ ...prev, platform: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="twitter">Twitter</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Media Type</label>
                  <Select
                    value={newPost.media_type}
                    onValueChange={(value) => setNewPost(prev => ({ ...prev, media_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="carousel">Carousel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Post ID</label>
                  <Input
                    value={newPost.post_id}
                    onChange={(e) => setNewPost(prev => ({ ...prev, post_id: e.target.value }))}
                    placeholder="Platform post ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Posted Date</label>
                  <Input
                    type="date"
                    value={newPost.posted_at}
                    onChange={(e) => setNewPost(prev => ({ ...prev, posted_at: e.target.value }))}
                  />
                </div>

                <div className="md:col-span-2">
                  <ImageUpload
                    bucketName="admin-uploads"
                    currentImage={newPost.media_url}
                    onUploadSuccess={(url) => setNewPost(prev => ({ ...prev, media_url: url }))}
                    label="Media Upload"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Post URL</label>
                  <Input
                    value={newPost.post_url}
                    onChange={(e) => setNewPost(prev => ({ ...prev, post_url: e.target.value }))}
                    placeholder="Link to original post"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Likes Count</label>
                  <Input
                    type="number"
                    value={newPost.likes_count}
                    onChange={(e) => setNewPost(prev => ({ ...prev, likes_count: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Comments Count</label>
                  <Input
                    type="number"
                    value={newPost.comments_count}
                    onChange={(e) => setNewPost(prev => ({ ...prev, comments_count: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Caption</label>
                <Textarea
                  value={newPost.caption}
                  onChange={(e) => setNewPost(prev => ({ ...prev, caption: e.target.value }))}
                  placeholder="Post caption..."
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={newPost.is_featured}
                  onCheckedChange={(checked) => setNewPost(prev => ({ ...prev, is_featured: checked }))}
                />
                <label className="text-sm font-medium">Featured Post</label>
              </div>

              <Button
                onClick={() => savePost.mutate()}
                disabled={!newPost.platform || !newPost.media_url || !newPost.post_url || savePost.isPending}
              >
                {savePost.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {editingPost ? "Update Post" : "Add Post"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>Manage Social Media Posts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {posts.map((post) => (
              <div key={post.id} className="border rounded-lg p-4 flex items-start gap-4">
                <img
                  src={post.media_url}
                  alt="Post media"
                  className="w-20 h-20 object-cover rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{post.platform}</span>
                    <span className="text-sm text-gray-500">({post.media_type})</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{post.caption}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{post.likes_count} likes</span>
                    <span>{post.comments_count} comments</span>
                    <span>{new Date(post.posted_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={post.is_featured}
                      onCheckedChange={(checked) => toggleStatus.mutate({ id: post.id, field: 'is_featured', value: checked })}
                    />
                    <span className="text-sm">Featured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={post.is_active}
                      onCheckedChange={(checked) => toggleStatus.mutate({ id: post.id, field: 'is_active', value: checked })}
                    />
                    <span className="text-sm">Active</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(post)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deletePost.mutate(post.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Default export for SocialMediaPostsManager component
export default SocialMediaPostsManager;
